import { pageIntroContent } from '@insify/css/classes.module.css';
import { PastClaims, RichContentWithTooltip, useWizard } from '@insify/funnel-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCommands } from '../../hooks/useCommands';
import { useConditionsQuery, useParamsQuery } from '../../hooks/useQueries';

import { usePastClaimsContent } from './usePastClaimsContent';

import type { PastClaim } from '../../models/pastClaim';
import type { CountryCode } from '@insify/global-countries';

const emptyClaims: PastClaim[] = [];

export const PastClaimsPage = () => {
  const { t, i18n } = useTranslation(['funnel']);
  const pastClaimsContent = usePastClaimsContent();
  const { showPreviousPage, showNextPage } = useWizard();
  const { sendCommand, isLoading } = useCommands();
  const { data: { pastClaimsThreshold } = { pastClaimsThreshold: { cents: '0', currency: 'EUR' } } } =
    useConditionsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);
  const { data: { pastClaims = emptyClaims, pastClaimsSumBelowThresholdConfirmed } = {}, isStale } = useParamsQuery();

  const hasPastClaims =
    pastClaimsSumBelowThresholdConfirmed === undefined ? undefined : !pastClaimsSumBelowThresholdConfirmed;

  useEffect(() => {
    if (triggerShowingNextPage && !isStale && !isLoading) {
      showNextPage();
      setTriggerShowingNextPage(false);
    }
  }, [triggerShowingNextPage, isStale, isLoading]);

  return (
    <PastClaims
      {...pastClaimsContent}
      currencyPosition='after'
      thousandSeparator=' '
      country={i18n.language as Lowercase<CountryCode>}
      pastClaimThreshold={pastClaimsThreshold}
      onBack={showPreviousPage}
      pastClaims={pastClaims}
      coverageOptions={[
        { label: t('funnel:past_claims_option_professional_or_public_liability'), value: 'professionalLiability' },
      ]}
      hasPastClaims={hasPastClaims}
      isSubmitting={isSubmitting}
      eventHandlers={{
        onSubmit: async (pastClaims) => {
          setIsSubmitting(true);
          await sendCommand({
            type: 'confirmPastClaimsSumWithinThreshold',
            payload: { pastClaimsSumBelowThreshold: pastClaims.length === 0 },
          });
          setTriggerShowingNextPage(true);
        },
        onAdd: (claim) => sendCommand({ type: 'addPastClaim', payload: { pastClaim: claim } }),
        onUpdate: (claim) => sendCommand({ type: 'changePastClaim', payload: { pastClaim: claim } }),
        onRemove: (claim) => sendCommand({ type: 'removePastClaim', payload: { id: claim.id } }),
      }}
    >
      <RichContentWithTooltip content={pastClaimsContent.richContent.overview} className={pageIntroContent} />
    </PastClaims>
  );
};
